/**
 * StudioAnalytics — YouTube-Studio-style overview across all channels.
 * KPI tiles, time-series chart, top videos, weekly comparison, health score.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid,
} from "recharts";
import {
  BarChart3, Eye, Clock, MousePointer, Users, Video, TrendingUp, Activity,
  Loader2, AlertTriangle, Play, Calendar, ChevronRight,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import "./studio.css";

const API = process.env.REACT_APP_BACKEND_URL || '';

function fmtNum(n) {
  if (n == null) return "—";
  if (n >= 1e6) return (n / 1e6).toFixed(1) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "K";
  return String(n);
}
function fmtDuration(sec) {
  if (!sec) return "—";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s}s`;
}

export default function StudioAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    axios
      .get(`${API}/api/studio/analytics/overview?range_days=${days}`, { withCredentials: true })
      .then((r) => { if (alive) setData(r.data); })
      .catch((err) => { if (alive) setError(err?.response?.data?.detail || err.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days]);

  const kpis = data?.kpis || {};
  const series = data?.time_series || [];
  const top = data?.top_videos || [];
  const wk = data?.weekly_comparison || {};

  return (
    <>
      <Header />
      <div className="studio-shell">
        <div className="studio-hero" style={{ paddingBottom: 12 }}>
          <span className="studio-eyebrow"><Activity size={12}/> Analytics · YouTube Studio-style</span>
          <h1 className="studio-h1">
            All your numbers, <span className="studio-h1-accent">at one glance.</span>
          </h1>
          <p className="studio-sub">
            Cross-channel views, watch time, CTR, growth and health score in one view.
          </p>
          <div className="studio-hero-cta">
            <select className="studio-input" value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="an-range">
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 180 days</option>
              <option value={365}>Last 365 days</option>
            </select>
            <Link to="/studio/reports" className="studio-btn-ghost">Reports →</Link>
          </div>
        </div>

        {error && <div className="ac-error"><AlertTriangle size={16}/> {error}</div>}
        {loading && (
          <div data-testid="an-skeletons">
            <div className="studio-ops-row">
              {[1,2,3,4,5,6].map((i) => <div key={i} className="studio-skeleton studio-sk-card"/>)}
            </div>
            <div className="studio-skeleton" style={{ height: 280, marginTop: 16 }}/>
          </div>
        )}

        {!loading && data && (
          <>
            <div className="studio-ops-row" data-testid="an-kpis">
              <Kpi label="Total views" val={fmtNum(kpis.total_views)} icon={Eye}/>
              <Kpi label="Watch time" val={`${fmtNum(kpis.total_watch_minutes)}m`} icon={Clock}/>
              <Kpi label="Avg CTR" val={`${((kpis.avg_ctr || 0) * 100).toFixed(1)}%`} icon={MousePointer}/>
              <Kpi label="Subscribers" val={fmtNum(kpis.subscribers)} icon={Users}/>
              <Kpi label="Uploads / week" val={kpis.upload_frequency_per_week} icon={Video}/>
              <Kpi label="Avg view duration" val={fmtDuration(kpis.avg_view_duration_sec)} icon={Activity}/>
              <Kpi label="Growth · 7d" val={`${kpis.growth_rate_pct > 0 ? "+" : ""}${kpis.growth_rate_pct}%`} icon={TrendingUp}
                   tone={kpis.growth_rate_pct >= 0 ? "good" : "bad"}/>
              <HealthCard score={kpis.channel_health_score}/>
            </div>

            <div className="studio-section-h"><h2><BarChart3 size={16}/> Views & uploads · time series</h2></div>
            <div className="studio-card" style={{ padding: 14 }} data-testid="an-timeseries">
              {series.length === 0 ? (
                <p className="studio-muted" style={{ padding: 20, textAlign: "center" }}>
                  No published videos in this window.
                </p>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={series}>
                    <defs>
                      <linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#a78bfa" stopOpacity={0.6}/>
                        <stop offset="100%" stopColor="#a78bfa" stopOpacity={0.05}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="rgba(167,139,250,0.08)" vertical={false}/>
                    <XAxis dataKey="date" stroke="#94A3B8" tick={{ fontSize: 11 }}/>
                    <YAxis stroke="#94A3B8" tick={{ fontSize: 11 }}/>
                    <Tooltip
                      contentStyle={{ background: "#FFFFFF", border: "1px solid rgba(167,139,250,0.3)", borderRadius: 8 }}
                      labelStyle={{ color: "#0F172A" }}
                      itemStyle={{ color: "#475569" }}
                    />
                    <Area type="monotone" dataKey="views" stroke="#a78bfa" fill="url(#viewsGrad)" strokeWidth={2}/>
                    <Area type="monotone" dataKey="uploads" stroke="#ec7c5e" fill="transparent" strokeWidth={2}/>
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="studio-section-h" style={{ marginTop: 22 }}>
              <h2><Calendar size={16}/> Weekly comparison</h2>
            </div>
            <div className="studio-card" style={{ padding: 14 }} data-testid="an-weekly">
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={[{ name: "Prev 7d", v: wk.prev_week || 0 }, { name: "This 7d", v: wk.this_week || 0 }]}>
                  <CartesianGrid stroke="rgba(167,139,250,0.08)" vertical={false}/>
                  <XAxis dataKey="name" stroke="#94A3B8" tick={{ fontSize: 11 }}/>
                  <YAxis stroke="#94A3B8" tick={{ fontSize: 11 }}/>
                  <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid rgba(167,139,250,0.3)", borderRadius: 8 }} />
                  <Bar dataKey="v" fill="#a78bfa" radius={[6, 6, 0, 0]} maxBarSize={80}/>
                </BarChart>
              </ResponsiveContainer>
              <p className="studio-muted" style={{ textAlign: "center", marginTop: 8 }}>
                {wk.delta_pct > 0 ? "📈" : wk.delta_pct < 0 ? "📉" : "➖"}
                <strong style={{ color: wk.delta_pct >= 0 ? "#16a34a" : "#ef4444", marginLeft: 6 }}>
                  {wk.delta_pct > 0 ? "+" : ""}{wk.delta_pct}%
                </strong>
                {" "}vs previous week
              </p>
            </div>

            <div className="studio-section-h" style={{ marginTop: 22 }}><h2><Play size={16}/> Top videos</h2></div>
            {top.length === 0 ? (
              <p className="studio-muted">No published videos in this window yet.</p>
            ) : (
              <div className="studio-pipe-table-wrap" data-testid="an-top-table">
                <table className="studio-pipe-table">
                  <thead>
                    <tr>
                      <th>#</th><th>Title</th><th>Views</th><th>CTR</th>
                      <th>Watch</th><th>Likes</th><th>Comments</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {top.map((v, i) => (
                      <tr key={v._id} data-testid={`an-top-row-${v._id}`}>
                        <td>#{i + 1}</td>
                        <td>{v.title}</td>
                        <td>{fmtNum(v.views)}</td>
                        <td>{v.ctr ? `${(v.ctr * 100).toFixed(1)}%` : "—"}</td>
                        <td>{v.watch_time ? `${fmtNum(Math.round(v.watch_time / 60))}m` : "—"}</td>
                        <td>{fmtNum(v.likes)}</td>
                        <td>{fmtNum(v.comments)}</td>
                        <td>
                          {v.youtube_video_id && (
                            <a
                              href={`https://www.youtube.com/watch?v=${v.youtube_video_id}`}
                              target="_blank" rel="noreferrer"
                              className="studio-btn-ghost"
                              style={{ padding: "4px 8px" }}
                              data-testid={`an-top-open-${v._id}`}
                            >
                              <Play size={12}/> Open <ChevronRight size={12}/>
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </>
  );
}

function Kpi({ label, val, icon: Icon, tone }) {
  return (
    <div className="studio-ops-card" data-testid={`an-kpi-${label.toLowerCase().replace(/\W+/g, "-")}`}>
      <header><span className="studio-eyebrow"><Icon size={12}/> {label}</span></header>
      <p style={{
        fontSize: 24, fontWeight: 800, margin: "6px 0 0",
        color: tone === "good" ? "#16a34a" : tone === "bad" ? "#ef4444" : "var(--st-text)",
      }}>{val}</p>
    </div>
  );
}

function HealthCard({ score }) {
  const s = score || 0;
  const tone = s >= 70 ? "#16a34a" : s >= 40 ? "#f59e0b" : "#ef4444";
  return (
    <div className="studio-ops-card" data-testid="an-kpi-health">
      <header><span className="studio-eyebrow"><Activity size={12}/> Channel health</span></header>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6 }}>
        <p style={{ fontSize: 24, fontWeight: 800, margin: 0, color: tone }}>{s}/100</p>
      </div>
      <div className="studio-progress" style={{ width: "100%", marginTop: 8, height: 8 }}>
        <div style={{ width: `${s}%`, background: tone }}/>
      </div>
    </div>
  );
}
